
import { useState, useEffect, useRef } from 'react';
import { cropOptions } from '../../../mocks/roiDefaults';
import type { CalculatorInputs } from './CalculatorForm';

interface YearData {
  year: number;
  annualSavings: number;
  annualCost: number;
  netSavings: number;
  cumulative: number;
}

function formatCurrency(n: number) {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    maximumFractionDigits: 0,
  }).format(n);
}

function formatCompact(n: number) {
  if (n >= 1000000) return `$${(n / 1000000).toFixed(1)}M`;
  if (n >= 1000) return `$${(n / 1000).toFixed(0)}K`;
  return formatCurrency(n);
}

/**
 * Builds year-by-year projection data.
 * Year 1 includes one-time assessment savings; subsequent years do not.
 * A conservative 2% annual inflation is applied to recurring savings.
 */
function buildProjection(inputs: CalculatorInputs, years: number): YearData[] {
  const acreage = inputs.acreage > 0 ? inputs.acreage : 0;
  const applications = inputs.applicationsPerSeason > 0 ? inputs.applicationsPerSeason : 0;
  const crop = cropOptions.find(c => c.value === inputs.cropType) || cropOptions[0];
  const grossPerAcre = crop.yieldPerAcre * crop.pricePerBushel;

  // Recurring annual savings
  let compactionSaved = 0;
  if (
    inputs.selectedServices.includes('aerial') &&
    ['ground', 'aerial-plane', 'manual'].includes(inputs.currentMethod)
  ) {
    compactionSaved = grossPerAcre * 0.04 * acreage;
  }

  let timingSaved = 0;
  if (
    inputs.selectedServices.includes('timing') &&
    inputs.currentMethod !== 'none' &&
    applications > 0
  ) {
    const criticalWindows = Math.min(applications, 2);
    timingSaved = grossPerAcre * 0.08 * acreage * (criticalWindows / 2);
  }

  let deerSaved = 0;
  if (inputs.selectedServices.includes('deer')) {
    deerSaved = acreage * 0.08 * 150;
  }

  // One-time savings (Year 1 only)
  let assessmentSaved = 0;
  if (inputs.selectedServices.includes('assessment')) {
    assessmentSaved = acreage * 0.25 * 65;
  }

  const recurringSavings = compactionSaved + timingSaved + deerSaved;

  // Service costs
  const aerialCost = inputs.selectedServices.includes('aerial')
    ? acreage * 12 * applications
    : 0;
  const deerCost = inputs.selectedServices.includes('deer')
    ? acreage * 0.08 * 150
    : 0;
  const assessmentCost = inputs.selectedServices.includes('assessment')
    ? acreage * 3.5
    : 0;
  const totalServiceCost = aerialCost + deerCost + assessmentCost;

  const currentMethodCostPerAcre: Record<string, number> = {
    ground: 8,
    'aerial-plane': 18,
    manual: 25,
    none: 0,
  };
  const currentMethodCost =
    (currentMethodCostPerAcre[inputs.currentMethod] || 0) * acreage * applications;

  const baseNetCost = totalServiceCost - currentMethodCost;
  const inflationRate = 0.02;

  const data: YearData[] = [];
  let cumulative = 0;

  for (let y = 1; y <= years; y++) {
    const inflationMultiplier = Math.pow(1 + inflationRate, y - 1);
    const yearSavings =
      recurringSavings * inflationMultiplier + (y === 1 ? assessmentSaved : 0);
    const yearCost = baseNetCost * inflationMultiplier;
    const net = yearSavings - yearCost;
    cumulative += net;

    data.push({
      year: y,
      annualSavings: Math.round(yearSavings),
      annualCost: Math.round(yearCost),
      netSavings: Math.round(net),
      cumulative: Math.round(cumulative),
    });
  }

  return data;
}

interface BarProps {
  label: string;
  savings: number;
  cost: number;
  net: number;
  cumulative: number;
  maxCumulative: number;
  delay: number;
  isVisible: boolean;
}

function ProjectionBar({
  label,
  savings,
  cost,
  net,
  cumulative,
  maxCumulative,
  delay,
  isVisible,
}: BarProps) {
  const pct = maxCumulative > 0 ? (cumulative / maxCumulative) * 100 : 0;

  return (
    <div className="group">
      <div className="flex items-center justify-between mb-1.5">
        <span className="text-sm font-semibold text-gray-700">{label}</span>
        <div className="flex items-center gap-4">
          <span className="text-xs text-gray-400 hidden sm:inline">
            Net: {formatCurrency(net)}
          </span>
          <span className="text-sm font-bold text-emerald-800">
            {formatCurrency(cumulative)}
          </span>
        </div>
      </div>
      <div className="relative w-full bg-gray-100 rounded-full h-7 overflow-hidden">
        <div
          className="h-full rounded-full transition-all ease-out flex items-center justify-end pr-3"
          style={{
            width: isVisible ? `${Math.max(pct, 8)}%` : '0%',
            transitionDuration: '900ms',
            transitionDelay: `${delay}ms`,
            background: `linear-gradient(90deg, #059669 0%, #047857 100%)`,
          }}
        >
          {pct > 25 && (
            <span className="text-xs font-bold text-white/90 whitespace-nowrap">
              {formatCompact(cumulative)}
            </span>
          )}
        </div>
      </div>
      {/* Hover tooltip */}
      <div className="mt-1 flex items-center gap-3 text-xs text-gray-400 opacity-0 group-hover:opacity-100 transition-opacity">
        <span>
          <i className="ri-arrow-up-line text-emerald-600"></i> Savings:{' '}
          {formatCurrency(savings)}
        </span>
        <span>
          <i className="ri-arrow-down-line text-red-400"></i> Cost:{' '}
          {formatCurrency(cost)}
        </span>
      </div>
    </div>
  );
}

interface Props {
  inputs: CalculatorInputs;
}

export default function CumulativeSavingsChart({ inputs }: Props) {
  const [activeTab, setActiveTab] = useState<3 | 5>(5);
  const [isVisible, setIsVisible] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  const data3 = buildProjection(inputs, 3);
  const data5 = buildProjection(inputs, 5);
  const activeData = activeTab === 3 ? data3 : data5;

  const maxCumulative = Math.max(...activeData.map(d => d.cumulative), 1);
  const totalCumulative3 = data3[data3.length - 1]?.cumulative || 0;
  const totalCumulative5 = data5[data5.length - 1]?.cumulative || 0;

  // Intersection observer for animation
  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) setIsVisible(true);
      },
      { threshold: 0.2 },
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  // Reset animation on tab change
  useEffect(() => {
    setIsVisible(false);
    const t = setTimeout(() => setIsVisible(true), 50);
    return () => clearTimeout(t);
  }, [activeTab]);

  return (
    <div
      ref={containerRef}
      className="bg-white border border-gray-200 rounded-lg overflow-hidden"
    >
      {/* Header */}
      <div className="px-6 pt-6 pb-4 border-b border-gray-100">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h3 className="text-lg font-bold text-gray-900">
              Cumulative Savings Projection
            </h3>
            <p className="text-xs text-gray-500 mt-1">
              Includes 2% annual inflation adjustment on recurring benefits
            </p>
          </div>
          {/* Tab Switcher */}
          <div className="bg-gray-100 rounded-full p-1 flex shrink-0">
            <button
              onClick={() => setActiveTab(3)}
              className={`px-4 py-1.5 rounded-full text-sm font-semibold transition-all cursor-pointer whitespace-nowrap ${
                activeTab === 3
                  ? 'bg-white text-emerald-800 shadow-sm'
                  : 'text-gray-500 hover:text-gray-700'
              }`}
            >
              3-Year
            </button>
            <button
              onClick={() => setActiveTab(5)}
              className={`px-4 py-1.5 rounded-full text-sm font-semibold transition-all cursor-pointer whitespace-nowrap ${
                activeTab === 5
                  ? 'bg-white text-emerald-800 shadow-sm'
                  : 'text-gray-500 hover:text-gray-700'
              }`}
            >
              5-Year
            </button>
          </div>
        </div>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-2 gap-4 px-6 pt-5 pb-2">
        <div
          className={`rounded-lg p-4 text-center transition-all cursor-pointer ${
            activeTab === 3
              ? 'bg-emerald-50 ring-2 ring-emerald-200'
              : 'bg-stone-50 hover:bg-stone-100'
          }`}
          onClick={() => setActiveTab(3)}
        >
          <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1">
            3-Year Total
          </p>
          <div className="text-2xl md:text-3xl font-bold text-emerald-800">
            {formatCompact(totalCumulative3)}
          </div>
          <p className="text-xs text-gray-400 mt-1">
            ~{formatCurrency(Math.round(totalCumulative3 / 3))}/yr avg
          </p>
        </div>
        <div
          className={`rounded-lg p-4 text-center transition-all cursor-pointer ${
            activeTab === 5
              ? 'bg-emerald-50 ring-2 ring-emerald-200'
              : 'bg-stone-50 hover:bg-stone-100'
          }`}
          onClick={() => setActiveTab(5)}
        >
          <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1">
            5-Year Total
          </p>
          <div className="text-2xl md:text-3xl font-bold text-emerald-800">
            {formatCompact(totalCumulative5)}
          </div>
          <p className="text-xs text-gray-400 mt-1">
            ~{formatCurrency(Math.round(totalCumulative5 / 5))}/yr avg
          </p>
        </div>
      </div>

      {/* Bar Chart */}
      <div className="px-6 py-5 space-y-4">
        {activeData.map((d, i) => (
          <ProjectionBar
            key={`${activeTab}-${d.year}`}
            label={`Year ${d.year}`}
            savings={d.annualSavings}
            cost={d.annualCost}
            net={d.netSavings}
            cumulative={d.cumulative}
            maxCumulative={maxCumulative}
            delay={i * 120}
            isVisible={isVisible}
          />
        ))}
      </div>

      {/* Year-over-Year Table */}
      <div className="px-6 pb-6">
        <div className="border border-gray-100 rounded-lg overflow-hidden">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-stone-50">
                <th className="text-left px-4 py-2.5 text-xs font-semibold text-gray-500 uppercase tracking-wider">
                  Year
                </th>
                <th className="text-right px-4 py-2.5 text-xs font-semibold text-gray-500 uppercase tracking-wider">
                  Gross Savings
                </th>
                <th className="text-right px-4 py-2.5 text-xs font-semibold text-gray-500 uppercase tracking-wider">
                  Net Cost
                </th>
                <th className="text-right px-4 py-2.5 text-xs font-semibold text-gray-500 uppercase tracking-wider">
                  Net Benefit
                </th>
                <th className="text-right px-4 py-2.5 text-xs font-semibold text-emerald-700 uppercase tracking-wider">
                  Cumulative
                </th>
              </tr>
            </thead>
            <tbody>
              {activeData.map((d) => (
                <tr
                  key={`table-${activeTab}-${d.year}`}
                  className="border-t border-gray-50 hover:bg-emerald-50/30 transition-colors"
                >
                  <td className="px-4 py-2.5 font-semibold text-gray-700">
                    Year {d.year}
                  </td>
                  <td className="px-4 py-2.5 text-right text-gray-600">
                    {formatCurrency(d.annualSavings)}
                  </td>
                  <td className="px-4 py-2.5 text-right text-gray-600">
                    {formatCurrency(d.annualCost)}
                  </td>
                  <td className="px-4 py-2.5 text-right font-medium text-gray-800">
                    {formatCurrency(d.netSavings)}
                  </td>
                  <td className="px-4 py-2.5 text-right font-bold text-emerald-800">
                    {formatCurrency(d.cumulative)}
                  </td>
                </tr>
              ))}
            </tbody>
            <tfoot>
              <tr className="border-t-2 border-emerald-200 bg-emerald-50/50">
                <td className="px-4 py-3 font-bold text-gray-900">
                  {activeTab}-Year Total
                </td>
                <td className="px-4 py-3 text-right font-bold text-gray-700">
                  {formatCurrency(
                    activeData.reduce((s, d) => s + d.annualSavings, 0),
                  )}
                </td>
                <td className="px-4 py-3 text-right font-bold text-gray-700">
                  {formatCurrency(
                    activeData.reduce((s, d) => s + d.annualCost, 0),
                  )}
                </td>
                <td className="px-4 py-3 text-right font-bold text-gray-800">
                  {formatCurrency(
                    activeData.reduce((s, d) => s + d.netSavings, 0),
                  )}
                </td>
                <td className="px-4 py-3 text-right font-bold text-emerald-800 text-lg">
                  {formatCurrency(
                    activeData[activeData.length - 1]?.cumulative || 0,
                  )}
                </td>
              </tr>
            </tfoot>
          </table>
        </div>
      </div>

      {/* Footnote */}
      <div className="px-6 pb-5">
        <div className="flex items-start gap-2 text-xs text-gray-400">
          <div className="w-4 h-4 flex items-center justify-center shrink-0 mt-0.5">
            <i className="ri-information-line"></i>
          </div>
          <p>
            Projections assume consistent acreage and service selections year
            over year. Assessment &amp; imaging savings are applied in Year 1
            only. Recurring savings include a conservative 2% annual inflation
            adjustment. Actual results will vary.
          </p>
        </div>
      </div>
    </div>
  );
}
